import axios from 'axios';

import getAuthSession from './auth';
import { PartnerResource, PartnerResourceMetadata, ResourceImageType } from '../interfaces/partnerResources.interface';
import { fileNameFromUrl } from '../utils/fileUtils';

type QueryParamValue = string | number | boolean | Array<string | number>;
type QueryParams = Record<string, QueryParamValue>;
type GridFilterItem = {
  columnField?: string;
  operatorValue?: string;
  value?: unknown;
};

type GridFilterModel = {
  items?: GridFilterItem[];
};

type FetchPartnerResourcesArgs = {
  categoryId?: number;
  authorId?: number;
  version?: string;
  ordering?: string;
  limit?: number;
  offset?: number;
  filterModel?: GridFilterModel;
};

type PartnerResourceCreatePayload =
  Omit<PartnerResource, 'id' | 'createdAt' | 'updatedAt' | 'metadata' | 'images' | 'category' | 'author' | 'subDescription'> & {
    category: number;
    author: number | null;
    sub_description: string;
  };

export const RESOURCE_CATEGORY_TITLES = {
  POLICIES: 'POLICIES',
  KEY_TERMS: 'KEY TERMS & DEFINITIONS',
  MEMBER_DISCOUNTS: 'MEMBER GROUP DISCOUNTS',
  BEST_PRACTICES: 'MPA BEST PRACTICES LANGUAGE TRANSLATIONS',
};

// Prefer the API's presigned attachment URL
export const getResourceDocumentDownload = (
  resource: PartnerResource,
): { url: string; filename: string } | null => {
  const doc = resource.images.find((image) => image.type === ResourceImageType.DOCUMENT);
  if (!doc) return null;

  const url = doc.downloadUrl || doc.image;
  const filename = doc.originalFilename || fileNameFromUrl(doc.image) || 'download.pdf';
  return { url, filename };
};

export async function postDownloadPartnerResource(partnerResourceMetadata: PartnerResourceMetadata) {
  const authSession = await getAuthSession();
  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BASE_API}/metadata/`,
      partnerResourceMetadata,
      {
        headers: {
          'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
        },
      },
    );
    return data as PartnerResourceMetadata;
  } catch (error) {
    console.error(error);
  }
}

export default async function fetchPartnerResources(args: FetchPartnerResourcesArgs = {}) {
  const authSession = await getAuthSession();

  const params: QueryParams = {
    limit: args.limit ?? 9999,
  };

  if (args.offset !== undefined) params.offset = args.offset;
  if (args.ordering) params.ordering = args.ordering;

  if (args.categoryId !== undefined) params.category = args.categoryId;
  if (args.authorId !== undefined) params.author = args.authorId;
  if (args.version) params.version = args.version;

  const items = args.filterModel?.items ?? [];
  items.forEach((item) => {
    if (!item.columnField) return;
    if (!item.operatorValue) return;
    if (item.value === null) return;

    const apiField = item.columnField;
    params[apiField] = String(item.value);
  });

  const res = await axios.get(`${process.env.REACT_APP_BASE_API}/resources/`, {
    params,
    headers: {
      Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
    },
  });

  return res.data;
}

export const fetchResourceCategories = async () => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.get(
      `${process.env.REACT_APP_BASE_API}/resource-categories/`,
      {
        headers: {
          'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};

export const patchPartnerResourceImages = async (
  resourceId: number,
  image?: File | null,
  doc?: File | null,
) => {
  const authSession = await getAuthSession();
  const fd = new FormData();
  if (image) fd.append('image', image);
  if (doc) fd.append('document', doc);

  try {
    const { data } = await axios.patch(
      `${process.env.REACT_APP_BASE_API}/resources/${resourceId}/images/`,
      fd,
      {
        headers: {
          Authorization: `Bearer ${authSession.getIdToken().getJwtToken()}`,
        },
      },
    );
    return data;
  } catch (error) {
    console.error(error);
  }
};

export const fetchSortedPartnerResources = async (): Promise<PartnerResource[]> => {
  const { results } = await fetchPartnerResources();
  if (!results) return [];

  return [...results].sort((a, b) => a.title.localeCompare(b.title));
};

export const postPartnerResource = async (
  resource: PartnerResourceCreatePayload,
  image?: File | null,
  doc?: File | null,
) => {
  const authSession = await getAuthSession();

  const fd = new FormData();

  Object.entries(resource).forEach(([k, v]) => {
    if (v === undefined || v === null) return;
    fd.append(k, String(v));
  });

  if (image) fd.append('image', image);
  if (doc) fd.append('document', doc);

  try {
    const { data } = await axios.post(
      `${process.env.REACT_APP_BASE_API}/resources/`,
      fd,
      {
        headers: {
          'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
        },
      },
    );
    return data;
  } catch (error) {
    console.error(error);
  }
};

export const deletePartnerResource = async (resourceId: number) => {
  const authSession = await getAuthSession();
  try {
    await axios.delete(
      `${process.env.REACT_APP_BASE_API}/resources/${resourceId}/`,
      {
        headers: {
          'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
        },
      },
    );
    return true;
  } catch (error) {
    console.error(error);
    return false;
  }
};

export const patchPartnerResource = async (resourceId: number, updatedFields: Partial<PartnerResource>) => {
  const authSession = await getAuthSession();
  try {
    const response = await axios.patch(
      `${process.env.REACT_APP_BASE_API}/resources/${resourceId}/`,
      updatedFields,
      {
        headers: {
          'Authorization': `Bearer ${authSession.getIdToken().getJwtToken()}`,
          'Content-Type': 'multipart/form-data',
        },
      },
    );
    return response.data;
  } catch (error) {
    console.error(error);
  }
};